import React, { useState } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Empty,
  Typography,
  Tabs,
  Table,
  Image,
  Card,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ItemDetailModalProps } from '../../../types/item-master-data';
import {
  STATUS_COLORS,
  HAS_EDIT_COLORS,
  TIME_FORMAT,
  NUMBER_FORMAT_CONFIG,
} from '../../../utils/itemMasterDataConstants';
import dayjs from 'dayjs';

const { Text } = Typography;

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ visible, item, onClose }) => {
  const { t } = useTranslation();

  // 获取商品类型翻译的辅助函数
  const getItemTypeTranslation = (itemType: string) => {
    if (!itemType) return itemType;
    
    // 根据实际的itemType值映射到翻译键值
    const typeMap: { [key: string]: string } = {
      'location+facing': 'location+facing',
      'location only': 'location only',
      'storage only': 'storage only'
    };
    
    const mappedType = typeMap[itemType] || itemType;
    const translationKey = `itemMasterData.itemType.${mappedType}`;
    const translation = t(translationKey);
    
    // 如果翻译键值不存在，返回原始值
    return translation === translationKey ? itemType : translation;
  };
  const [activeTab, setActiveTab] = useState('basic');

  if (!item) return null;

  // 格式化时间
  const formatTime = (timestamp: number) => {
    return dayjs(timestamp).format(TIME_FORMAT.dateTime);
  };

  // 格式化数字
  const formatNumber = (value: number, type: 'price' | 'weight' | 'dimension' | 'volume') => {
    const config = NUMBER_FORMAT_CONFIG[type];
    const formattedValue = value?.toFixed(config?.precision);
    
    
    return formattedValue;
  };

  // 基本信息
  const BasicInfoContent = () => (
    <Card>
      <Descriptions title={t('itemMasterData.detail.basicInfo')} bordered column={2}>
        <Descriptions.Item label={t('itemMasterData.detail.ownerCode')}>{item.ownerCode}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.itemCode')}>
          <Text copyable={{ text: item.code }}>{item.code}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.itemName')}>{item.name}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.englishName')}>{item.englishName}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.stockUnit')}>{item.stockUnit}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.itemType')}>
          {getItemTypeTranslation(item.itemType)}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.status')}>
          <Tag color={STATUS_COLORS[item.status]}>{item.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.hasEdit')}>
          <Tag color={HAS_EDIT_COLORS[item.hasEdit]}>{t(`itemMasterData.hasEdit.${item.hasEdit}`)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.displayType')}>
          {t(`itemMasterData.displayType.${item.displayType}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.price')}>
          {formatNumber(item.retailPrice, 'price')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.brandCode')}>{item.brandCode}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.brandName')}>{item.brandName}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.group')}>{item.group}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.packaging')}>{item.packaging}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.packSize')}>{item.packSize}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.strategicFlag')}>
          <Tag color={item.strategicFlag ? 'green' : 'default'}>
            {item.strategicFlag ? t('itemMasterData.detail.yes') : t('itemMasterData.detail.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.firstReceivedTime')}>
          {formatTime(item.firstReceivedTime)}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.selfBrandFlag')}>
          <Tag color={item.selfBrandFlag ? 'purple' : 'default'}>
            {item.selfBrandFlag ? t('itemMasterData.detail.yes') : t('itemMasterData.detail.no')}
          </Tag>
        </Descriptions.Item>
        {item.remark && (
          <Descriptions.Item label={t('itemMasterData.detail.remark')} span={2}>
            {item.remark}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );

  // 物理特性
  const PhysicalContent = () => (
    <Card>
      <Descriptions title={t('itemMasterData.detail.physicalProperties')} bordered column={2}>
        <Descriptions.Item label={t('itemMasterData.detail.length')}>
          {formatNumber(item.length, 'dimension')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.width')}>
          {formatNumber(item.width, 'dimension')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.height')}>
          {formatNumber(item.height, 'dimension')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.volume')}>
          {formatNumber(item.volume, 'volume')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.grossWeight')}>
          {formatNumber(item.grossWeight, 'weight')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.netWeight')}>
          {formatNumber(item.netWeight, 'weight')}
        </Descriptions.Item>
        
        {/* PDQ尺寸信息 */}
        <Descriptions.Item label={t('itemMasterData.detail.pdqLength')}>
          {formatNumber(item.length4PDQ, 'dimension')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.pdqWidth')}>
          {formatNumber(item.width4PDQ, 'dimension')}
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.pdqHeight')}>
          {formatNumber(item.height4PDQ, 'dimension')}
        </Descriptions.Item>
        
        {/* 其他规格 */}
        {(item.size || item.weightOnLabel || item.capacity) && (
          <>
            {item.size && (
              <Descriptions.Item label={t('itemMasterData.detail.size')}>{item.size}</Descriptions.Item>
            )}
            {item.weightOnLabel && (
              <Descriptions.Item label={t('itemMasterData.detail.weightOnLabel')}>{item.weightOnLabel}</Descriptions.Item>
            )}
            {item.capacity && (
              <Descriptions.Item label={t('itemMasterData.detail.capacity')}>{item.capacity}</Descriptions.Item>
            )}
          </>
        )}
      </Descriptions>
    </Card>
  );

  // 类目信息
  const CategoryContent = () => (
    <Card>
      <Descriptions title={t('itemMasterData.detail.categoryInfo')} bordered column={1}>
        <Descriptions.Item label={t('itemMasterData.detail.category1')}>
          <Text strong>{item.category1Name}</Text> ({item.category1Id})
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.category2')}>
          <Text strong>{item.category2Name}</Text> ({item.category2Id})
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.category3')}>
          <Text strong>{item.category3Name}</Text> ({item.category3Id})
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.category4')}>
          <Text strong>{item.category4Name}</Text> ({item.category4Id})
        </Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.category5')}>
          <Text strong>{item.category5Name}</Text> ({item.category5Id})
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 商品属性
  const AttributesContent = () => (
    <Card>
      <Descriptions title={t('itemMasterData.detail.attributes')} bordered column={2}>
        <Descriptions.Item label={t('itemMasterData.detail.group')}>{item.group}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.packaging')}>{item.packaging}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.packSize')}>{item.packSize}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.size')}>{item.size}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.weightOnLabel')}>{item.weightOnLabel}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.capacity')}>{item.capacity}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.flavor')}>{item.flavor}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.color')}>{item.color}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.pattern')}>{item.pattern}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.smell')}>{item.smell}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.variants')}>{item.variants}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.attribute16')}>{item.attribute16}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.attribute17')}>{item.attribute17}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.attribute18')}>{item.attribute18}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.attribute19')}>{item.attribute19}</Descriptions.Item>
        <Descriptions.Item label={t('itemMasterData.detail.attribute20')}>{item.attribute20}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // 图片展示
  const ImagesContent = () => (
    <Card>
      {item.imageList && item.imageList.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
          gap: 16,
          padding: '16px 0'
        }}>
          {item.imageList.map((imageUrl, index) => (
            <div key={index} style={{ textAlign: 'center' }}>
              <Image
                src={imageUrl}
                alt={t('itemMasterData.detail.itemImage') + `${index + 1}`}
                style={{ width: '100%', height: 120, objectFit: 'cover', border: '1px solid #d9d9d9' }}
                placeholder
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {t('itemMasterData.detail.image')} {index + 1}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty description={t('itemMasterData.detail.noImages')} />
      )}
    </Card>
  );

  const tabItems = [
    {
      key: 'basic',
      label: t('itemMasterData.detail.basicInfo'),
      children: <BasicInfoContent />,
    },
    {
      key: 'physical',
      label: t('itemMasterData.detail.physicalProperties'),
      children: <PhysicalContent />,
    },
    {
      key: 'category',
      label: t('itemMasterData.detail.categoryInfo'),
      children: <CategoryContent />,
    },
    {
      key: 'attributes',
      label: t('itemMasterData.detail.attributes'),
      children: <AttributesContent />,
    },
    {
      key: 'images',
      label: t('itemMasterData.detail.images'),
      children: <ImagesContent />,
    },
  ];

  return (
    <Modal
      title={`${t('itemMasterData.detail.modalTitle')} - ${item.code}`}
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <Tabs
        items={tabItems}
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};

export default ItemDetailModal;